import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import PublicLandingPage from "./pages/PublicLandingPage";
import AboutPage from "./pages/AboutPage";
import PricingPage from "./pages/PricingPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import ProductTourPage from "./pages/ProductTourPage";
import LoginPage from "./pages/LoginPage";
import SignInPage from "./pages/SignInPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import RequisitionsPage from "./pages/RequisitionsPage";
import VendorsPage from "./pages/VendorsPage";
import PurchaseOrdersPage from "./pages/PurchaseOrdersPage";
import PurchaseOrderDetailPage from "./pages/PurchaseOrderDetailPage";
import VendorPortalPage from "./pages/VendorPortalPage";
import FreightPortalPage from "./pages/FreightPortalPage";
import FCApprovalQueuePage from "./pages/FCApprovalQueuePage";
import BuyerPortalPage from "./pages/BuyerPortalPage";
import DepartmentHeadPortalPage from "./pages/DepartmentHeadPortalPage";
import ReceiverPortalPage from "./pages/ReceiverPortalPage";
import StorekeeperPortalPage from "./pages/StorekeeperPortalPage";
import InternalRequesterPortalPage from "./pages/InternalRequesterPortalPage";
import UserManagementPage from "./pages/UserManagementPage";
import ShipmentsPage from "./pages/ShipmentsPage";
import FreightForwardersPage from "./pages/FreightForwardersPage";
import ClosedPOsPage from "./pages/ClosedPOsPage";
import ReportsPage from "./pages/ReportsPage";
import SettingsPage from "./pages/SettingsPage";
import DigitalCountPage from "./pages/DigitalCountPage";
import SandboxLoginPage from "./pages/SandboxLoginPage";
import SandboxPortalPage from "./pages/SandboxPortalPage";
import SandboxManagementPage from "./pages/SandboxManagementPage";
import Layout from "./components/Layout";
import "./App.css";

const getDefaultRouteForRole = (role) => {
  switch (role) {
    case "vendor":
      return "/vendor-portal";
    case "freight_forwarder":
      return "/freight-portal";
    case "buyer":
      return "/buyer-portal";
    case "department_head":
      return "/dh-portal";
    case "receiver":
      return "/receiver-portal";
    case "accounts_payable":
      return "/receiver-portal";
    case "storekeeper":
      return "/storekeeper-portal";
    case "financial_controller":
      return "/fc-approval";
    case "internal_requester":
      return "/internal-requester-portal";
    default:
      return "/dashboard";
  }
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3D1D72] border-t-transparent"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/demo" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={getDefaultRouteForRole(user.role)} replace />;
  }
  
  return children;
};

// Public route - redirects authenticated users to their default page
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#3D1D72] border-t-transparent"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to={getDefaultRouteForRole(user.role)} replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public landing page - no login button, only tour and demo request */}
      <Route path="/" element={<PublicLandingPage />} />
      
      {/* Public legal pages */}
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      
      {/* Product Tour - accessible to everyone */}
      <Route path="/tour" element={<ProductTourPage />} />
      
      {/* Demo login page - always accessible for role switching */}
      <Route path="/demo" element={<LoginPage />} />
      
      {/* Dedicated sign-in page (separate from /demo) */}
      <Route path="/login" element={<SignInPage />} />
      <Route path="/signin" element={<Navigate to="/login" replace />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Sandbox (public) — branded login page + dashboard stub per slot */}
      <Route path="/sandbox/:slot" element={<SandboxLoginPage />} />
      <Route path="/sandbox/:slot/dashboard" element={<SandboxPortalPage />} />
      
      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={["purchasing_manager", "department_head", "financial_controller", "system_admin"]}>
          <Layout><DashboardPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/requisitions" element={
        <ProtectedRoute allowedRoles={["purchasing_manager", "department_head", "system_admin"]}>
          <Layout><RequisitionsPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/vendors" element={
        <ProtectedRoute allowedRoles={["purchasing_manager", "system_admin"]}>
          <Layout><VendorsPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/purchase-orders" element={
        <ProtectedRoute allowedRoles={["purchasing_manager", "financial_controller", "system_admin"]}>
          <Layout><PurchaseOrdersPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/purchase-orders/:poId" element={
        <ProtectedRoute allowedRoles={["purchasing_manager", "financial_controller", "system_admin"]}>
          <Layout><PurchaseOrderDetailPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/vendor-portal" element={
        <ProtectedRoute allowedRoles={["vendor"]}>
          <Layout><VendorPortalPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/freight-portal" element={
        <ProtectedRoute allowedRoles={["freight_forwarder"]}>
          <Layout><FreightPortalPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/fc-approval" element={
        <ProtectedRoute allowedRoles={["financial_controller", "system_admin"]}>
          <Layout><FCApprovalQueuePage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/buyer-portal" element={
        <ProtectedRoute allowedRoles={["buyer"]}>
          <Layout><BuyerPortalPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/dh-portal" element={
        <ProtectedRoute allowedRoles={["department_head"]}>
          <Layout><DepartmentHeadPortalPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/receiver-portal" element={
        <ProtectedRoute allowedRoles={["receiver", "purchasing_manager", "system_admin", "accounts_payable"]}>
          <Layout><ReceiverPortalPage /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/receiving-records" element={
        <ProtectedRoute allowedRoles={["purchasing_manager", "receiver", "system_admin"]}>
          <Layout><ReceiverPortalPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/storekeeper-portal" element={
        <ProtectedRoute allowedRoles={["storekeeper", "purchasing_manager", "system_admin"]}>
          <Layout><StorekeeperPortalPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/internal-requester-portal" element={
        <ProtectedRoute allowedRoles={["internal_requester"]}>
          <Layout><InternalRequesterPortalPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/shipments" element={
        <ProtectedRoute allowedRoles={["purchasing_manager", "system_admin"]}>
          <Layout><ShipmentsPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/freight-forwarders" element={
        <ProtectedRoute allowedRoles={["purchasing_manager", "system_admin"]}>
          <Layout><FreightForwardersPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/closed-pos" element={
        <ProtectedRoute allowedRoles={["purchasing_manager", "financial_controller", "system_admin"]}>
          <Layout><ClosedPOsPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/reports" element={
        <ProtectedRoute allowedRoles={["purchasing_manager", "financial_controller", "department_head", "storekeeper", "system_admin"]}>
          <Layout><ReportsPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout><SettingsPage /></Layout>
        </ProtectedRoute>
      } />
      
      <Route path="/user-management" element={
        <ProtectedRoute allowedRoles={["purchasing_manager", "system_admin"]}>
          <Layout><UserManagementPage /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/admin/sandboxes" element={
        <ProtectedRoute allowedRoles={["purchasing_manager", "system_admin"]}>
          <Layout><SandboxManagementPage /></Layout>
        </ProtectedRoute>
      } />

      <Route path="/digital-count" element={
        <ProtectedRoute allowedRoles={["purchasing_manager", "storekeeper", "financial_controller", "system_admin"]}>
          <Layout><DigitalCountPage /></Layout>
        </ProtectedRoute>
      } />
      
      {/* Catch all - redirect to landing page */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster 
          position="top-right" 
          richColors 
          toastOptions={{
            style: {
              fontFamily: "'DM Sans', sans-serif",
            },
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
// Build: v2.12.1 - April 6, 2026 14:05 UTC - JSON body stream fix deployed
